// services/reportingAPI.js - Enhanced with better error handling and validation
import { api } from "../../../api/";

export const reportingAPI = {
  // Dashboard stats
  getDashboardStats: async () => {
    try {
      const response = await api.get('/reporting/dashboard-stats', { timeout: 10000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  },

  // Scheduled reports
  getScheduledReports: async () => {
    try {
      const response = await api.get('/reporting/scheduled', { timeout: 10000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching scheduled reports:', error);
      throw error;
    }
  },

  createScheduledReport: async (reportData) => {
    try {
      // Validate required fields before sending
      if (!reportData.name || reportData.name.trim() === '') {
        throw new Error('Report name is required');
      }
      
      if (!reportData.business_id) {
        throw new Error('Business ID is required');
      }
      
      if (!reportData.created_by) {
        throw new Error('Created by field is required');
      }
      
      if (!reportData.recipients || !Array.isArray(reportData.recipients) || reportData.recipients.length === 0) {
        throw new Error('At least one recipient email is required');
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = reportData.recipients.filter(email => !emailRegex.test(email));
      if (invalidEmails.length > 0) {
        throw new Error(`Invalid email addresses: ${invalidEmails.join(', ')}`);
      }
      
      console.log('Sending create request with data:', reportData);
      
      const response = await api.post('/reporting/scheduled', reportData, { timeout: 15000 });
      return response.data;
    } catch (error) {
      console.error('Error creating scheduled report:', error);
      console.error('Request data was:', reportData);
      
      // Re-throw with additional context
      if (error.response?.data?.error) {
        const serverError = new Error(error.response.data.error);
        serverError.response = error.response;
        throw serverError;
      }
      throw error;
    }
  },

  updateScheduledReport: async (id, reportData) => {
    try {
      // Validate required fields before sending
      if (reportData.recipients && (!Array.isArray(reportData.recipients) || reportData.recipients.length === 0)) {
        throw new Error('At least one recipient email is required');
      }
      
      if (reportData.recipients) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidEmails = reportData.recipients.filter(email => !emailRegex.test(email));
        if (invalidEmails.length > 0) {
          throw new Error(`Invalid email addresses: ${invalidEmails.join(', ')}`);
        }
      }
      
      console.log('Sending update request with data:', reportData);
      
      const response = await api.put(`/reporting/scheduled/${id}`, reportData, { timeout: 10000 });
      return response.data;
    } catch (error) {
      console.error('Error updating scheduled report:', error);
      
      if (error.response?.data?.error) {
        const serverError = new Error(error.response.data.error);
        serverError.response = error.response;
        throw serverError;
      }
      throw error;
    }
  },

  deleteScheduledReport: async (id) => {
    try {
      const response = await api.delete(`/reporting/scheduled/${id}`, { timeout: 10000 });
      return response.data;
    } catch (error) {
      console.error('Error deleting scheduled report:', error);
      throw error;
    }
  },

  toggleScheduledReport: async (id, status) => {
    try {
      if (!['active', 'paused'].includes(status)) {
        throw new Error('Status must be either "active" or "paused"');
      }
      
      const response = await api.patch(`/reporting/scheduled/${id}/toggle`, { status }, { timeout: 10000 });
      return response.data;
    } catch (error) {
      console.error('Error toggling scheduled report:', error);
      throw error;
    }
  },

  // Recent activity
  getRecentActivity: async (limit = 20) => {
    try {
      const response = await api.get(`/reporting/recent-activity?limit=${limit}`, { timeout: 10000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      throw error;
    }
  },

  // Manual report execution
  executeReport: async (reportData) => {
    try {
      // Validate required fields
      if (!reportData.report_type || !reportData.name || !reportData.triggered_by) {
        throw new Error('Missing required fields: report_type, name, triggered_by');
      }
      
      const response = await api.post('/reporting/execute', reportData, { timeout: 15000 });
      return response.data;
    } catch (error) {
      console.error('Error executing report:', error);
      throw error;
    }
  },

  // Get specific scheduled report
  getScheduledReports: async (businessId = null) => {
    try {
      const params = businessId ? `?business_id=${businessId}` : '';
      const response = await api.get(`/reporting/scheduled${params}`, { timeout: 10000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching scheduled reports:', error);
      throw error;
    }
  },

  // Keep your existing method for fetching a single report
  getScheduledReport: async (id) => {
    try {
      const response = await api.get(`/reporting/scheduled/${id}`, { timeout: 10000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching scheduled report:', error);
      throw error;
    }
  },

  // Get latest execution for a scheduled report
  getLatestExecution: async (id) => {
    try {
      const response = await api.get(`/reporting/scheduled/${id}/latest-execution`, { timeout: 10000 });
      return response.data;
    } catch (error) {
      console.error('Error fetching latest execution:', error);
      throw error;
    }
  },

  // Download report
  downloadReport: async (filename) => {
    try {
      const response = await api.get(`/reports/download/${filename}`, {
        responseType: 'blob',
        timeout: 30000
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading report:', error);
      throw error;
    }
  },

  // Update analytics (when report is viewed)
  updateReportAnalytics: async (executionId, type = 'view') => {
    try {
      if (!['view', 'download', 'share'].includes(type)) {
        throw new Error('Invalid analytics type');
      }
      
      const response = await api.post(`/reporting/analytics/${executionId}/${type}`, {}, { timeout: 10000 });
      return response.data;
    } catch (error) {
      console.error('Error updating report analytics:', error);
      throw error;
    }
  },

  // Generate report
  generateReport: async (reportData) => {
    try {
      // Validate required fields
      if (!reportData.businessId) {
        throw new Error('Business ID is required for report generation');
      }
      
      const response = await api.post('/reports/generate', reportData, { timeout: 60000 });
      return response.data;
    } catch (error) {
      console.error('Error generating report:', error);
      throw error;
    }
  },

  // Cleanup report file
  cleanupReport: async (filename) => {
    try {
      const response = await api.delete(`/reports/cleanup/${filename}`, { timeout: 10000 });
      return response.data;
    } catch (error) {
      console.error('Error cleaning up report:', error);
      // Don't throw error for cleanup failures
      return { success: false, error: error.message };
    }
  },

  // Health check
  healthCheck: async () => {
    try {
      const response = await api.get('/reporting/health', { timeout: 10000 });
      return response.data;
    } catch (error) {
      console.error('Error checking reporting API health:', error);
      throw error;
    }
  }
};